import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { ITraining, BaseVideoFormData } from '../../types';
import { apiService } from '../../api/api.service';
import { arrayMove } from '@dnd-kit/sortable';

interface BonusVideosState {
    videos: ITraining[];
    selectedVideo: ITraining | null;
    editingVideo: ITraining | null;
    isLoading: boolean;
    isSaving: boolean;
    error: string | null;
    deleteDialog: {
        open: boolean;
        videoId: number | null;
        title: string;
    };
    openDialog: boolean;
    originalOrder: number[]; // Храним исходный порядок ID видео
}

const initialState: BonusVideosState = {
    videos: [],
    selectedVideo: null,
    editingVideo: null,
    isLoading: false,
    isSaving: false,
    error: null,
    deleteDialog: {
        open: false,
        videoId: null,
        title: ''
    },
    openDialog: false,
    originalOrder: []
};

// Async thunks
export const fetchBonusVideos = createAsyncThunk(
    'bonusVideos/fetchAll',
    async (language: string, { rejectWithValue }) => {
        try {
            const data = await apiService.getVideoList('bonus');
            return {
                data,
                language
            };
        } catch (error) {
            return rejectWithValue('Failed to fetch bonus videos');
        }
    }
);

export const addBonusVideo = createAsyncThunk(
    'bonusVideos/add',
    async ({
        training,
        language,
        videoFile,
        thumbnailFile
    }: {
        training: BaseVideoFormData['training'],
        language: string,
        videoFile?: File,
        thumbnailFile?: File
    }, { rejectWithValue }) => {
        try {
            let video_file_path = undefined;
            let preview_file_path = undefined;

            // Загружаем видео файл, если он есть
            if (videoFile) {
                const videoResponse = await apiService.uploadBonusVideoFile(videoFile);
                video_file_path = videoResponse.file_path;
            }

            // Загружаем превью файл, если он есть
            if (thumbnailFile) {
                const previewResponse = await apiService.uploadBonusPreviewFile(thumbnailFile);
                preview_file_path = previewResponse.file_path;
            }

            const trainingWithMetadata = {
                ...training,
                language: language.toUpperCase(),
                video_file_path,
                preview_file_path
            };

            const response = await apiService.uploadVideo('bonus', trainingWithMetadata);
            return response;
        } catch (error) {
            return rejectWithValue('Failed to add bonus video');
        }
    }
);

export const editBonusVideo = createAsyncThunk(
    'bonusVideos/edit',
    async ({
        id,
        training,
        language,
        videoFile,
        thumbnailFile
    }: {
        id: number;
        training: BaseVideoFormData['training'],
        language: string,
        videoFile?: File,
        thumbnailFile?: File
    }, { rejectWithValue }) => {
        try {
            let video_file_path = undefined;
            let preview_file_path = undefined;

            // Загружаем видео файл, если он есть
            if (videoFile) {
                const videoResponse = await apiService.uploadBonusVideoFile(videoFile);
                video_file_path = videoResponse.file_path;
            }

            // Загружаем превью файл, если он есть
            if (thumbnailFile) {
                const previewResponse = await apiService.uploadBonusPreviewFile(thumbnailFile);
                preview_file_path = previewResponse.file_path;
            }

            const trainingWithMetadata = {
                ...training,
                language: language.toUpperCase(),
                video_file_path,
                preview_file_path
            };

            const response = await apiService.editVideo('bonus', id, trainingWithMetadata);
            return response;
        } catch (error) {
            return rejectWithValue('Failed to edit bonus video');
        }
    }
);

export const deleteBonusVideo = createAsyncThunk(
    'bonusVideos/delete',
    async (id: number, { rejectWithValue }) => {
        try {
            await apiService.deleteVideo('bonus', id);
            return id;
        } catch (error) {
            return rejectWithValue('Failed to delete bonus video');
        }
    }
);

export const saveAllBonusVideos = createAsyncThunk(
    'bonusVideos/saveAll',
    async (_, { dispatch, getState, rejectWithValue }) => {
        try {
            const state = getState() as { bonusVideos: BonusVideosState };
            // Save videos with their visual order (sorted by `order`), normalizing to 1..n
            const videosSorted = [...state.bonusVideos.videos].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            const videosToSave = videosSorted.map((video, index) => ({
                ...video,
                order: index + 1 // Ensure contiguous order starting from 1
            }));

            // Call the API to save the order
            const language = localStorage.getItem('admin_language') || 'ru';
            const requestData = videosToSave.map(video => ({
                id: video.id,
                order: video.order,
                time: "10:00" // Default time value
            }));

            if (requestData.length > 0) {
                await apiService.saveVideosOrder(requestData);
            }

            // Refresh the videos list
            await dispatch(fetchBonusVideos(language));
            return true;
        } catch (error) {
            return rejectWithValue('Failed to save all videos');
        }
    }
);

const bonusVideosSlice = createSlice({
    name: 'bonusVideos',
    initialState,
    reducers: {
        setSelectedVideo: (state, action: PayloadAction<ITraining | null>) => {
            state.selectedVideo = action.payload;
        },
        setEditingVideo: (state, action: PayloadAction<ITraining | null>) => {
            state.editingVideo = action.payload;
            state.openDialog = !!action.payload;
        },
        setOpenDialog: (state, action: PayloadAction<boolean>) => {
            state.openDialog = action.payload;
            if (!action.payload) {
                state.editingVideo = null;
            }
        },
        setDeleteDialog: (state, action: PayloadAction<{ open: boolean; videoId: number | null; title: string }>) => {
            state.deleteDialog = action.payload;
        },
        reorderVideos: (state, action: PayloadAction<{ activeId: string; overId: string }>) => {
            const { activeId, overId } = action.payload;

            // Work with a list sorted by current `order` (visual order)
            const sortedByOrder = [...state.videos].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            const idsByOrder = sortedByOrder.map(v => v.id.toString());

            const oldIndex = idsByOrder.findIndex(id => id === activeId);
            const newIndex = idsByOrder.findIndex(id => id === overId);

            if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
                const movedIds = arrayMove(idsByOrder, oldIndex, newIndex);
                // Apply new contiguous order to videos by id
                const idToNewOrder: Record<string, number> = {};
                movedIds.forEach((id, index) => {
                    idToNewOrder[id] = index + 1;
                });

                state.videos = state.videos.map(video => ({
                    ...video,
                    order: idToNewOrder[video.id.toString()] ?? video.order
                }));

                // Optionally keep internal array in visual order to avoid mismatches
                state.videos.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            }
        },
        updateVideoFromStatus: (state, action: PayloadAction<{
            videoId: number;
            updates: Partial<ITraining>
        }>) => {
            const { videoId, updates } = action.payload;

            const videoIndex = state.videos.findIndex(v => v.id === videoId);
            if (videoIndex !== -1) {
                state.videos[videoIndex] = {
                    ...state.videos[videoIndex],
                    ...updates
                };
            }
        }
    },
    extraReducers: (builder) => {
        builder
            // fetchBonusVideos
            .addCase(fetchBonusVideos.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchBonusVideos.fulfilled, (state, action) => {
                const { data, language } = action.payload;
                state.videos = data
                    .filter(video => video.language && video.language.toUpperCase() === language.toUpperCase())
                    // Normalize: ensure we keep videos ordered by `order` for consistent UI/state
                    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                // Save baseline original order as visual order (sorted by `order`)
                state.originalOrder = state.videos.map(video => video.id);
                state.isLoading = false;
            })
            .addCase(fetchBonusVideos.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })

            // addBonusVideo
            .addCase(addBonusVideo.fulfilled, (state, action) => {
                state.videos = [action.payload, ...state.videos]
                    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                state.openDialog = false;
                // Обновляем исходный порядок после добавления (визуальный порядок)
                state.originalOrder = state.videos.map(video => video.id);
            })

            // editBonusVideo
            .addCase(editBonusVideo.fulfilled, (state, action) => {
                state.videos = state.videos.map(v =>
                    v.id === action.payload.id ? action.payload : v
                );
                state.editingVideo = null;
                state.openDialog = false;
            })

            // deleteBonusVideo
            .addCase(deleteBonusVideo.fulfilled, (state, action) => {
                state.videos = state.videos
                    .filter(v => v.id !== action.payload)
                    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                state.deleteDialog = {
                    open: false,
                    videoId: null,
                    title: ''
                };
                // Обновляем исходный порядок после удаления (визуальный порядок)
                state.originalOrder = state.videos.map(video => video.id);
            })

            // saveAllBonusVideos
            .addCase(saveAllBonusVideos.pending, (state) => {
                state.isSaving = true;
            })
            .addCase(saveAllBonusVideos.fulfilled, (state) => {
                state.isSaving = false;
                // Обновляем исходный порядок после успешного сохранения (визуальный порядок)
                state.videos.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                state.originalOrder = state.videos.map(video => video.id);
            })
            .addCase(saveAllBonusVideos.rejected, (state, action) => {
                state.isSaving = false;
                state.error = action.payload as string;
            });
    }
});

export const {
    setSelectedVideo,
    setEditingVideo,
    setOpenDialog,
    setDeleteDialog,
    reorderVideos,
    updateVideoFromStatus
} = bonusVideosSlice.actions;

// Селектор для проверки изменений порядка
export const selectHasOrderChanges = (state: { bonusVideos: BonusVideosState }) => {
    const { videos, originalOrder } = state.bonusVideos;
    const currentOrder = videos.map(video => video.id);

    if (originalOrder.length !== currentOrder.length) {
        return true;
    }

    return originalOrder.some((id, index) => id !== currentOrder[index]);
};

export default bonusVideosSlice.reducer; 